import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  collection, getDocs, getDoc, addDoc, updateDoc, doc, query, where, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { getUserRoles } from '../../utils/roleHelpers';
import Button from '../shared/UI/Button';
import MultiSelect from '../shared/Forms/MultiSelect';
import { sendInternalNotification } from '../../services/pifService';
import { advanceDealStage } from '../../services/dealService';
import { getAllApprovalSequences } from '../../services/firestoreService';
import { formatDate, formatDateTime, getFriendlyError } from '../../utils/formatters';

// ─── Markdown styles (guarded single injection) ───────────────────────────────
const ecimStyles = `
.ecim-content { font-size: 14px; line-height: 1.75; color: var(--color-text); }
.ecim-content h1,.ecim-content h2,.ecim-content h3,.ecim-content h4 {
  color: var(--color-text); font-weight: 700; margin: 16px 0 8px;
}
.ecim-content h1 { font-size: 18px; }
.ecim-content h2 { font-size: 16px; }
.ecim-content h3 { font-size: 14px; }
.ecim-content h4 { font-size: 13px; }
.ecim-content p { margin: 0 0 12px; }
.ecim-content ul, .ecim-content ol { padding-left: 20px; margin: 0 0 12px; }
.ecim-content li { margin-bottom: 4px; }
.ecim-content strong { font-weight: 600; }
.ecim-content hr { border: none; border-top: 1px solid var(--color-border); margin: 16px 0; }
.ecim-content table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 13px; }
.ecim-content th { background: var(--color-border-light); padding: 6px 10px; text-align: left; font-weight: 600; border: 1px solid var(--color-border); }
.ecim-content td { padding: 6px 10px; border: 1px solid var(--color-border); }
`;
let ecimStylesInjected = false;
function injectEcimStyles() {
  if (ecimStylesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = ecimStyles;
  document.head.appendChild(style);
  ecimStylesInjected = true;
}

const STATUS_META = {
  draft: { label: 'Draft', cls: 'badge badge-default' },
  primary_review: { label: 'Awaiting Secondary Review', cls: 'badge badge-info' },
  secondary_review: { label: 'Awaiting HoP Review', cls: 'badge badge-warning' },
  hop_review: { label: 'Awaiting HoP Review', cls: 'badge badge-warning' },
  iic_review: { label: 'Under IIC Review', cls: 'badge badge-info' },
  approved: { label: 'Approved', cls: 'badge badge-success' },
};

// ─── One comment in the sidebar ───────────────────────────────────────────────
// Body is rendered as plain text (pre-wrap), not markdown: a comment is a task
// note, and the doc is an immutable record once written.
function CommentCard({ comment, canClose, onClose }) {
  const isClosed = comment.status === 'closed';
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
      background: 'var(--color-card)', padding: 10, marginBottom: 8,
      opacity: isClosed ? 0.7 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{comment.author_name || '—'}</span>
        <span className={isClosed ? 'badge badge-success' : 'badge badge-warning'} style={{ fontSize: 10 }}>
          {isClosed ? 'Closed' : 'Open'}
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
        {formatDateTime(comment.created_at)} · raised on v{comment.ecim_version_at_creation || 1}
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.5, margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>
        {comment.body}
      </p>
      {isClosed && comment.closed_by_name && (
        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 6 }}>
          Closed by {comment.closed_by_name} · {formatDateTime(comment.closed_at)}
        </div>
      )}
      {!isClosed && canClose && (
        <div style={{ marginTop: 8 }}>
          <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
        </div>
      )}
    </div>
  );
}

export default function ECIMPanel({ deal, dealId, userRole }) {
  injectEcimStyles();
  const { user, userProfile } = useAuth();
  const roles = getUserRoles(userProfile);

  const [loading, setLoading] = useState(true);
  const [ecim, setEcim] = useState(null);
  const [versions, setVersions] = useState([]);
  const [frameworks, setFrameworks] = useState({});
  const [approvalSequence, setApprovalSequence] = useState(null);
  const [fundShortName, setFundShortName] = useState('MIA');

  const [generating, setGenerating] = useState(false);
  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [editText, setEditText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [savingState, setSavingState] = useState('idle'); // idle|saving|saved
  const [actionLoading, setActionLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showConfirmRegen, setShowConfirmRegen] = useState(false);
  const [error, setError] = useState('');

  // ── Task comments (deals/{dealId}/ecim_comments) ──
  const [comments, setComments] = useState([]);
  const [commentError, setCommentError] = useState('');
  const [commentNotice, setCommentNotice] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newMentions, setNewMentions] = useState([]);
  const [mentionables, setMentionables] = useState([]);
  const [posting, setPosting] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const saveTimer = useRef(null);

  const performedByName =
    userProfile?.full_name || userProfile?.display_name || user?.email || '';

  const isPrimaryAnalyst = deal?.assigned_analyst_id === user?.uid;
  const isSecondaryAnalyst = deal?.peer_reviewer_id === user?.uid;
  const isHoP = roles.includes('head_of_pipeline') || roles.includes('admin');
  const isAdmin = roles.includes('admin');
  const canGenerate = roles.some(r =>
    ['analyst', 'head_of_pipeline', 'admin'].includes(r));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const curSnap = await getDocs(query(
        collection(db, 'deals', dealId, 'ecim'),
        where('is_current', '==', true)
      ));
      const current = curSnap.empty
        ? null
        : { id: curSnap.docs[0].id, ...curSnap.docs[0].data() };
      setEcim(current);
      setEditText(current?.content || '');
      setDirty(false);

      const allSnap = await getDocs(collection(db, 'deals', dealId, 'ecim'));
      setVersions(allSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.version || 0) - (a.version || 0)));

      // Frameworks (prerequisite check)
      const fwSnap = await getDocs(collection(db, 'deals', dealId, 'frameworks'));
      const fw = {};
      fwSnap.docs.forEach(d => { fw[d.id] = d.data(); });
      setFrameworks(fw);
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  // ── Comments: load. Kept out of load() so a comments failure can never
  // ── stop the memo itself from rendering.
  const loadComments = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'deals', dealId, 'ecim_comments'));
      // Newest-first: a task list is read for what is outstanding.
      setComments(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))));
    } catch (err) {
      setCommentError(getFriendlyError(err));
    }
  }, [dealId]);

  useEffect(() => { loadComments(); }, [loadComments]);

  // ── Mentionable people: exactly the set that can READ a comment per the
  // ── ecim_comments rule (isInternalTeam + hasFundAccess, or admin), so we
  // ── never email someone about a comment they cannot open. Targeted query
  // ── mirrors AnalystReviewForm.jsx:88-92 — the broad getAllUsers() needs
  // ── admin/principal rights.
  useEffect(() => {
    if (!deal?.fund_id) return;
    getDocs(query(
      collection(db, 'users'),
      where('fund_access', 'array-contains', deal.fund_id),
      where('is_active', '==', true)
    )).then(snap => {
      setMentionables(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => {
          const uRoles = u.roles || (u.role ? [u.role] : []);
          return uRoles.some(r =>
            ['analyst', 'head_of_pipeline', 'cio', 'md', 'admin'].includes(r)
          ) && u.id !== user?.uid;
        }));
    }).catch(err => {
      console.error('e-CIM comments: mentionable user lookup failed:', err);
    });
  }, [deal?.fund_id, user?.uid]);

  useEffect(() => {
    if (!deal?.fund_id) return;
    getDoc(doc(db, 'funds', deal.fund_id)).then(fundDoc => {
      if (fundDoc.exists()) {
        const f = fundDoc.data();
        setFundShortName(f.short_name || f.name || 'MIA');
      }
    }).catch(() => {});
  }, [deal?.fund_id]);

  // The fund's approval sequence — needed to open the IIC Sign-off gate on
  // "Advance to IIC" (D1: the first approver's role is config, never hardcoded).
  useEffect(() => {
    if (!deal?.fund_id) return;
    getAllApprovalSequences().then(seqs => {
      setApprovalSequence(seqs.find(seq => seq.fund_id === deal.fund_id) || null);
    }).catch(() => {});
  }, [deal?.fund_id]);

  // Editable states: draft (primary edits), primary_review (secondary edits)
  const status = ecim?.status || 'draft';
  const canEdit =
    (status === 'draft') ||
    (status === 'primary_review' && isSecondaryAnalyst);

  // ── Auto-save with 2s debounce ──
  const doSave = useCallback(async (text) => {
    if (!ecim) return;
    setSavingState('saving');
    try {
      await updateDoc(doc(db, 'deals', dealId, 'ecim', ecim.id), {
        content: text,
        last_edited_at: new Date().toISOString(),
        last_edited_by: user?.uid || null,
        last_edited_by_name: performedByName,
      });
      setSavingState('saved');
      setDirty(false);
      setTimeout(() => setSavingState('idle'), 1500);
    } catch (err) {
      setError(getFriendlyError(err));
      setSavingState('idle');
    }
  }, [ecim, dealId, user?.uid, performedByName]);

  useEffect(() => {
    if (!dirty || !canEdit) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { doSave(editText); }, 2000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [editText, dirty, canEdit, doSave]);

  async function handleGenerate() {
    setShowConfirmRegen(false);
    setGenerating(true);
    setError('');
    try {
      const fns = getFunctions();
      const genFn = httpsCallable(fns, 'generateECIM');
      await genFn({ dealId });
      await load();
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setGenerating(false);
    }
  }

  async function transition(updates) {
    if (!ecim) return;
    setActionLoading(true);
    setError('');
    try {
      // Flush any pending edits first
      if (dirty) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        await doSave(editText);
      }
      await updateDoc(doc(db, 'deals', dealId, 'ecim', ecim.id), updates);
      await load();
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setActionLoading(false);
    }
  }

  const handleConfirmPrimary = () => transition({
    status: 'primary_review',
    primary_analyst_id: user?.uid || null,
    primary_analyst_name: performedByName,
    primary_analyst_confirmed_at: new Date().toISOString(),
  });

  const handleSecondarySignOff = () => transition({
    status: 'secondary_review',
    secondary_analyst_id: user?.uid || null,
    secondary_analyst_name: performedByName,
    secondary_analyst_signed_off_at: new Date().toISOString(),
  });

  // The HoP advancing the e-CIM to IIC is what OPENS the IIC Sign-off gate
  // (gate:'ic1'). It (1) moves the e-CIM into IIC review (unchanged), and
  // (2) — only when the deal is still at 'screening' — advances the deal to
  // 'iic_approval_ic1' and creates the first ic1 approval doc, mirroring the
  // pre-screening gate in HOPReviewForm.jsx. The current_stage === 'screening'
  // guard makes a re-click / replay a no-op: it cannot create a duplicate
  // pending approval doc (and after the first run the button is gone anyway,
  // since ecim.status is no longer 'secondary_review').
  async function handleAdvanceToIIC() {
    if (!ecim) return;
    setActionLoading(true);
    setError('');
    try {
      // Flush any pending edits first (mirror transition()).
      if (dirty) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        await doSave(editText);
      }

      // (1) e-CIM enters IIC review — unchanged behaviour.
      await updateDoc(doc(db, 'deals', dealId, 'ecim', ecim.id), {
        status: 'iic_review',
        hop_reviewed_by: user?.uid || null,
        hop_reviewed_by_name: performedByName,
        hop_reviewed_at: new Date().toISOString(),
      });

      // (2) Open the IIC Sign-off gate, guarded so it runs exactly once.
      //     Re-read current_stage from Firestore rather than trusting the deal
      //     prop (which this panel does not refresh) — this closes the duplicate-
      //     doc window a stale prop would leave if load() failed after the writes.
      const freshDealSnap = await getDoc(doc(db, 'deals', dealId));
      const freshStage = freshDealSnap.exists() ? freshDealSnap.data().current_stage : null;
      if (freshStage === 'screening') {
        const firstStage = (approvalSequence?.stages || []).find(s => s.stage_order === 1);

        // Advance the deal (writes the audit entry — D3).
        await advanceDealStage({
          dealId,
          newStage: 'iic_approval_ic1',
          performedBy: user?.uid,
          performedByName,
          action: 'advanced_to_iic_signoff',
        });

        // First IIC approval document for the ic1 gate — mirrors HOPReviewForm.
        await addDoc(collection(db, 'deals', dealId, 'iic_approvals'), {
          deal_id: dealId,
          gate: 'ic1',
          stage_order: 1,
          approver_role: firstStage?.role || 'head_of_pipeline',
          approver_id: null,
          status: 'pending',
          notes: null,
          requested_at: serverTimestamp(),
          decided_at: null,
        });

        // Notify the first IIC approver (HoP themselves) — mirrors HOPReviewForm.
        await sendInternalNotification({
          recipientIds: [user?.uid],
          subject: `IIC Approval Required — ${deal.company_name}`,
          body: `Your IIC approval is required for ${deal.company_name}. Please log in to review and approve.`,
          dealId,
        }).catch(() => {});
      }

      await load();
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setActionLoading(false);
    }
  }

  // ── Comments: add. The comment is written and awaited FIRST; the email is
  // ── fired afterwards in its own try/catch, so a mail failure can never lose
  // ── the comment. (sendInternalNotification swallows SMTP errors and returns
  // ── success anyway — functions/index.js:2778-2799 — but we do not rely on that.)
  async function handleAddComment() {
    const body = newBody.trim();
    if (!body || !ecim) return;
    setPosting(true);
    setCommentError('');
    setCommentNotice('');
    try {
      await addDoc(collection(db, 'deals', dealId, 'ecim_comments'), {
        body,
        author_id: user?.uid || null,
        author_name: performedByName,
        created_at: new Date().toISOString(),
        status: 'open',
        closed_at: null,
        closed_by: null,
        closed_by_name: null,
        ecim_version_at_creation: ecim.version || 1,
        mentions: newMentions,
      });

      const mentioned = newMentions;
      setNewBody('');
      setNewMentions([]);
      await loadComments();

      if (mentioned.length) {
        try {
          await sendInternalNotification({
            recipientIds: mentioned,
            subject: `e-CIM Comment — ${deal.company_name}`,
            body: `${performedByName} mentioned you in a comment on the e-CIM for ${deal.company_name} (v${ecim.version || 1}):\n\n${body}\n\nPlease log in to the portal to respond.`,
            dealId,
          });
        } catch (mailErr) {
          console.error('e-CIM comment: mention notification failed:', mailErr);
          setCommentNotice('Comment saved, but the mention email could not be sent.');
        }
      }
    } catch (err) {
      setCommentError(getFriendlyError(err));
    } finally {
      setPosting(false);
    }
  }

  // ── Comments: close. Author or admin (rules enforce the same — firestore.rules
  // ── ecim_comments allow update). Comments are closed, never deleted.
  async function handleCloseComment(comment) {
    setCommentError('');
    try {
      await updateDoc(doc(db, 'deals', dealId, 'ecim_comments', comment.id), {
        status: 'closed',
        closed_at: new Date().toISOString(),
        closed_by: user?.uid || null,
        closed_by_name: performedByName,
      });
      await loadComments();
    } catch (err) {
      setCommentError(getFriendlyError(err));
    }
  }

  async function handleDownloadPDF() {
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const el = document.getElementById('ecim-pdf-content');
      if (!el) { alert('Could not find e-CIM content.'); return; }
      const today = new Date();
      const dateStr = today.getFullYear().toString() +
        String(today.getMonth() + 1).padStart(2, '0') +
        String(today.getDate()).padStart(2, '0');
      const cleanCompany = (deal.company_name || 'Company').replace(/[^a-zA-Z0-9]/g, '');
      const cleanFund = (fundShortName || 'MIA').replace(/[^a-zA-Z0-9]/g, '');
      const filename =
        `MIA_${cleanFund}_eCIM_${cleanCompany}_${dateStr}_v${ecim.version || 1}.pdf`;
      const options = {
        margin: [15, 15, 15, 15],
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };
      await html2pdf().set(options).from(el).save();
    } catch (err) {
      console.error('e-CIM PDF error:', err);
      alert('Could not generate PDF. Please try again.');
    }
  }

  if (loading) {
    return <p className="text-muted" style={{ fontSize: 13 }}>Loading e-CIM…</p>;
  }

  // ── STATE A — no e-CIM yet ──
  if (!ecim) {
    const dsqReady = !!deal?.dsq_company_submitted || deal?.dsq_score != null;
    const frameworksReady = !!frameworks.porters || !!frameworks.swot;
    return (
      <div className="card">
        <div className="card-header"><h2>Investment Committee Memorandum (e-CIM)</h2></div>
        <div className="card-body">
          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            The e-CIM is an AI-generated draft IC Memo, seeded from the PIF, DSQ responses,
            and completed analytical frameworks. It is drafted here for the investment team
            to review, edit, and sign off before presentation to the Investment Committee.
            Market statistics and financials are left as <em>[ANALYST TO COMPLETE]</em> placeholders —
            the AI does not invent data.
          </p>

          {!dsqReady && (
            <div className="alert alert-warning" style={{ marginBottom: 12 }}>
              ⚠ The DSQ has not yet been submitted for this deal. You can still generate a draft,
              but it will contain more placeholders.
            </div>
          )}
          {!frameworksReady && (
            <div className="alert alert-warning" style={{ marginBottom: 12 }}>
              ⚠ Key frameworks (Porter's / SWOT) are not yet completed. Complete them under the
              Frameworks tab for a richer draft.
            </div>
          )}

          {canGenerate ? (
            <Button variant="primary" loading={generating} onClick={handleGenerate}>
              {generating ? 'Generating e-CIM Draft…' : 'Generate e-CIM Draft'}
            </Button>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Only analysts or the Head of Pipeline can generate the e-CIM.
            </p>
          )}
          {generating && (
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 10 }}>
              This can take 20–40 seconds. Please wait…
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── STATES B–E — e-CIM exists ──
  const meta = STATUS_META[status] || STATUS_META.draft;
  const openComments = comments.filter(c => c.status !== 'closed');
  const closedComments = comments.filter(c => c.status === 'closed');
  const mentionOptions = mentionables.map(u => ({
    value: u.id,
    label: u.full_name || u.email || u.id,
  }));

  return (
    <div>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="card mb-4">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2>e-CIM</h2>
            <span className={meta.cls}>{meta.label}</span>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              v{ecim.version || 1} · generated {formatDate(ecim.generated_at)}
            </span>
            {savingState === 'saving' && (
              <span style={{ fontSize: 12, color: 'var(--color-info)' }}>Saving…</span>
            )}
            {savingState === 'saved' && (
              <span style={{ fontSize: 12, color: 'var(--color-success)' }}>✓ Saved</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={handleDownloadPDF}
              style={{
                padding: '7px 14px', borderRadius: 6, border: '1px solid #1e3a5f',
                background: '#1e3a5f', color: '#fff', cursor: 'pointer', fontSize: 13,
              }}
            >
              ↓ Download PDF
            </button>
            {canEdit && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setMode(m => (m === 'edit' ? 'view' : 'edit'))}
              >
                {mode === 'edit' ? 'Done' : 'Edit'}
              </Button>
            )}
            {status === 'draft' && canGenerate && (
              <Button size="sm" variant="secondary" loading={generating} onClick={() => setShowConfirmRegen(true)}>
                Regenerate
              </Button>
            )}
          </div>
        </div>

        <div className="card-body">
          {/* Sign-off chain summary */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 14, fontSize: 12, color: 'var(--color-text-secondary)' }}>
            <span>
              Primary: {ecim.primary_analyst_confirmed_at
                ? `✓ ${ecim.primary_analyst_name || '—'} (${formatDate(ecim.primary_analyst_confirmed_at)})`
                : '—'}
            </span>
            <span>
              Secondary: {ecim.secondary_analyst_signed_off_at
                ? `✓ ${ecim.secondary_analyst_name || '—'} (${formatDate(ecim.secondary_analyst_signed_off_at)})`
                : '—'}
            </span>
            <span>
              HoP: {ecim.hop_reviewed_at
                ? `✓ ${ecim.hop_reviewed_by_name || '—'} (${formatDate(ecim.hop_reviewed_at)})`
                : '—'}
            </span>
          </div>

          {/* Memo column + comment sidebar. The sidebar is a SIBLING of the memo
              column, so it is outside #ecim-pdf-content in both view and edit mode
              and can never reach the PDF. */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 460px', minWidth: 0 }}>
              {canEdit && mode === 'edit' ? (
                <>
                  <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                    Editing as {status === 'draft' ? 'primary analyst' : 'secondary analyst'} — changes auto-save after 2 seconds. Markdown supported.
                  </p>
                  <textarea
                    value={editText}
                    onChange={e => { setEditText(e.target.value); setDirty(true); }}
                    style={{
                      width: '100%', minHeight: 480, padding: '10px 12px',
                      border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
                      fontSize: 13, fontFamily: 'monospace', lineHeight: 1.6,
                      resize: 'vertical', boxSizing: 'border-box',
                    }}
                  />
                </>
              ) : (
                <div id="ecim-pdf-content" className="ecim-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {editText || ecim.content}
                  </ReactMarkdown>
                </div>
              )}

              {/* Hidden render target for PDF while the textarea is showing */}
              {canEdit && mode === 'edit' && (
                <div id="ecim-pdf-content" className="ecim-content" style={{ display: 'none' }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {editText || ecim.content}
                  </ReactMarkdown>
                </div>
              )}
            </div>

            <aside style={{
              flex: '0 0 320px', minWidth: 0,
              border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
              background: 'var(--color-border-light)', padding: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700 }}>
                  Tasks &amp; Comments
                </h3>
                <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {openComments.length} open
                </span>
              </div>

              {commentError && (
                <div className="alert alert-error" style={{ marginBottom: 10, fontSize: 12 }}>{commentError}</div>
              )}
              {commentNotice && (
                <div className="alert alert-warning" style={{ marginBottom: 10, fontSize: 12 }}>{commentNotice}</div>
              )}

              {/* Composer */}
              <textarea
                value={newBody}
                onChange={e => setNewBody(e.target.value)}
                placeholder="Raise a task or comment for the team…"
                style={{
                  width: '100%', minHeight: 70, padding: '8px 10px',
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
                  fontSize: 13, lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
                }}
              />
              <div style={{ marginTop: 8 }}>
                <MultiSelect
                  label="Notify (@mention)"
                  options={mentionOptions}
                  selected={newMentions}
                  onChange={setNewMentions}
                  hint={mentionOptions.length ? 'Mentioned people receive an email.' : 'No other team members with access to this fund.'}
                />
              </div>
              <Button
                size="sm"
                variant="primary"
                loading={posting}
                disabled={!newBody.trim()}
                onClick={handleAddComment}
              >
                Add Comment
              </Button>

              {/* List */}
              <div style={{ marginTop: 14 }}>
                {openComments.length === 0 && (
                  <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    No open tasks on this e-CIM.
                  </p>
                )}
                {openComments.map(c => (
                  <CommentCard
                    key={c.id}
                    comment={c}
                    canClose={c.author_id === user?.uid || isAdmin}
                    onClose={() => handleCloseComment(c)}
                  />
                ))}
              </div>

              {closedComments.length > 0 && (
                <div style={{ marginTop: 12, borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
                  <button
                    onClick={() => setShowClosed(s => !s)}
                    style={{
                      width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                      textAlign: 'left', fontSize: 12, color: 'var(--color-text-secondary)', padding: 0,
                    }}
                  >
                    {showClosed ? '▲ Hide' : '▼ Show'} closed ({closedComments.length})
                  </button>
                  {showClosed && (
                    <div style={{ marginTop: 8 }}>
                      {closedComments.map(c => (
                        <CommentCard key={c.id} comment={c} canClose={false} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </aside>
          </div>

          {/* Workflow action buttons */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
            {status === 'draft' && isPrimaryAnalyst && (
              <Button variant="primary" loading={actionLoading} onClick={handleConfirmPrimary}>
                Confirm as Primary Analyst
              </Button>
            )}
            {status === 'primary_review' && isSecondaryAnalyst && (
              <Button variant="primary" loading={actionLoading} onClick={handleSecondarySignOff}>
                Secondary Analyst Sign-Off
              </Button>
            )}
            {status === 'secondary_review' && isHoP && (
              <Button variant="primary" loading={actionLoading} onClick={handleAdvanceToIIC}>
                Advance to IIC
              </Button>
            )}
            {status === 'iic_review' && (
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                This e-CIM is now under IIC review. Record the IIC decision on the Approvals workflow.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Regenerate confirmation */}
      {showConfirmRegen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: 'var(--color-card)', borderRadius: 'var(--radius-lg)',
            padding: 24, maxWidth: 440, width: '100%', margin: 16, boxShadow: 'var(--shadow-md)',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Regenerate e-CIM?</h3>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
              This creates a new version from the latest deal data. The current draft becomes a
              historical version. Continue?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button size="sm" variant="secondary" onClick={() => setShowConfirmRegen(false)}>Cancel</Button>
              <Button size="sm" variant="primary" onClick={handleGenerate}>Regenerate</Button>
            </div>
          </div>
        </div>
      )}

      {/* Version history */}
      <div className="card">
        <button
          onClick={() => setShowHistory(s => !s)}
          style={{
            width: '100%', padding: '10px 14px', background: 'none', border: 'none',
            cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--color-text-secondary)',
          }}
        >
          {showHistory ? '▲ Hide' : '▼ Show'} version history ({versions.length})
        </button>
        {showHistory && (
          <div className="card-body" style={{ paddingTop: 0 }}>
            {versions.map(v => (
              <div key={v.id} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '8px 0', borderBottom: '1px solid var(--color-border-light)', fontSize: 13,
              }}>
                <span>
                  v{v.version || 1} · {(STATUS_META[v.status] || STATUS_META.draft).label}
                  {v.is_current && <span className="badge badge-success" style={{ marginLeft: 8 }}>Current</span>}
                </span>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {v.generated_by_name || '—'} · {formatDateTime(v.generated_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
